import { UserOutlined } from '@ant-design/icons';
import { Avatar, message, Upload, type UploadProps } from 'antd';
import { useField } from 'formik';
import { isObject } from 'lodash/fp';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { type UploadFileWithPreviewFormDataFragment } from '../../api/fragments/UploadFileWithPreviewFormData';
import FolderIcon from '../../assets/ci/profile/folder.svg';
import { validateExtensions } from '../../utilities/extensions';
import FormItem, { type FormItemProps } from './FormItem';

const Container = styled.div`
    display: flex;
    justify-content: center;
`;

const OverlayContainer = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    color: white;
    border-radius: 50%;

    &:hover {
        cursor: pointer;
    }
`;

export type ProfileImageUploadFieldProps = Omit<UploadProps, 'accept' | 'listType' | 'maxCount'> & {
    name: string;
    extensions: string[];
    label?: string;
    required?: boolean;
    getLink?: (field: string, documentId: string) => Promise<string>;
    sizeLimitInMiB?: number;
    tooltip?: FormItemProps['tooltip'];
    placeHolderText?: string;
};

const ProfileImageUploadField = ({
    name,
    extensions,
    label,
    required = false,
    getLink,
    sizeLimitInMiB = 2,
    tooltip,
    placeHolderText,
}: ProfileImageUploadFieldProps) => {
    const { t } = useTranslation(['common']);

    const [, meta, helpers] = useField<File | UploadFileWithPreviewFormDataFragment>(name);
    const { initialValue } = meta;
    const [preview, setPreview] = useState<string | ArrayBuffer | null>(
        !(initialValue instanceof File) && isObject(initialValue) ? initialValue.url : null
    );

    const accept = useMemo(() => extensions.join(','), [extensions]);

    const customRequest = useCallback<UploadProps['customRequest']>(
        ({ file, onSuccess }) => {
            if (file instanceof File) {
                const reader = new FileReader();

                reader.onload = ({ target }) => {
                    setPreview(target?.result);
                };

                reader.readAsDataURL(file as Blob);

                // set the file manually
                helpers.setValue(file);
                // touch the field manually
                helpers.setTouched(true);
                // do nothing on success
                onSuccess(null);
            }
        },
        [helpers]
    );

    const onRemove = useCallback(() => {
        setPreview(null);

        // manually touch the file
        helpers.setTouched(true);

        // delete the file
        helpers.setValue(null);
    }, [helpers]);

    const beforeUpload = useCallback<UploadProps['beforeUpload']>(
        file => {
            if (file.size > sizeLimitInMiB * 1024 * 1024) {
                message.error(t('common:upload.messages.sizeExceedsLimit', { limit: sizeLimitInMiB }));

                return Upload.LIST_IGNORE;
            }

            const reader = new FileReader();
            const promise = new Promise<boolean | string>((resolve, reject) => {
                reader.onload = async event => {
                    const content = event.target.result as string;
                    const validationResult = await validateExtensions(content, file.name);
                    if (validationResult.valid) {
                        return resolve(true);
                    }
                    message.error(t('common:upload.messages.fileError'));

                    return resolve(Upload.LIST_IGNORE);
                };

                reader.onerror = event => resolve(Upload.LIST_IGNORE);
            });

            reader.readAsBinaryString(file);

            return Promise.resolve(promise);
        },
        [sizeLimitInMiB, t]
    );

    const content = (
        <Container>
            <Upload
                accept={accept}
                beforeUpload={beforeUpload}
                customRequest={customRequest}
                name={name}
                onRemove={onRemove}
                showUploadList={false}
            >
                <div style={{ position: 'relative' }}>
                    <Avatar icon={<UserOutlined />} size={78} src={preview as string} />
                    <OverlayContainer>
                        <FolderIcon />
                        {placeHolderText}
                    </OverlayContainer>
                </div>
            </Upload>
        </Container>
    );

    return (
        <FormItem label={label} meta={meta} name={name} required={required} style={{ margin: '0' }} tooltip={tooltip}>
            {content}
        </FormItem>
    );
};

export default ProfileImageUploadField;
